import { useState, useEffect, useRef, useCallback } from 'react';

const DEBOUNCE_MS = 500;

function isNonEmpty<T extends object>(data: T, defaults: T): boolean {
  return Object.keys(defaults).some((key) => {
    const val = (data as any)[key];
    const def = (defaults as any)[key];
    if (val === def) return false;
    if (typeof val === 'string' && val.trim() === '') return false;
    if (Array.isArray(val) && val.length === 0 && Array.isArray(def) && def.length === 0) return false;
    return true;
  });
}

export interface UseFormDraftResult {
  draftRestored: boolean;
  dismissDraft: () => void;
  clearDraft: () => void;
}

/**
 * Autosaves form data to localStorage on every change (debounced).
 * On mount, if a draft exists, it calls `restoreData` so the consumer can
 * hydrate its own state.  Returns helpers for the "draft restored" banner.
 *
 * @param key       Unique localStorage key (e.g. "task-draft-global")
 * @param formData  Current form state — saved automatically on change
 * @param defaults  The initial/empty form state — used to detect "is the form dirty?"
 * @param restoreData  Called once on mount when a draft is found
 */
export default function useFormDraft<T extends object>(
  key: string,
  formData: T,
  defaults: T,
  restoreData: (saved: Partial<T>) => void,
): UseFormDraftResult {
  const [draftRestored, setDraftRestored] = useState(false);
  const restoredRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<T>;
      if (saved && typeof saved === 'object' && isNonEmpty(saved as T, defaults)) {
        restoreData(saved);
        setDraftRestored(true);
      }
    } catch {
      localStorage.removeItem(key);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      try {
        if (isNonEmpty(formData, defaults)) {
          localStorage.setItem(key, JSON.stringify(formData));
        } else {
          localStorage.removeItem(key);
        }
      } catch { /* quota exceeded — ignore */ }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [formData, key, defaults]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(key);
    setDraftRestored(false);
  }, [key]);

  const dismissDraft = useCallback(() => {
    setDraftRestored(false);
  }, []);

  return { draftRestored, dismissDraft, clearDraft };
}
